import React, { useState, useEffect } from 'react';
import { PageTracker } from '../../../components/PageTracker.tsx';
import {
  Plus,
  MessageSquare,
  ThumbsUp,
  Lightbulb,
  Target,
  Users,
  Clock,
  Trash2,
  CheckCircle2,
  Download,
  Search,
  Grid3X3,
  List,
  Calendar,
  Zap
} from 'lucide-react';

interface RetroItem {
  id: string;
  type: 'went-well' | 'could-improve' | 'action-items';
  content: string;
  author: string;
  votes: string[];
  timestamp: string;
  priority: 'low' | 'medium' | 'high';
  status?: 'pending' | 'in-progress' | 'completed';
  assignee?: string;
}

interface Sprint {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  team: string[];
  items: RetroItem[];
  isActive: boolean;
}

// ── Design tokens (matches Workflows.jsx / Kanban board exactly) ────────────
const T = {
  page:        "var(--lyra-color-surface)",
  surface:     "var(--lyra-color-surface)",
  surface2:    "var(--lyra-color-surface-2)",
  surface3:    "var(--app-surface-3)",
  border:      "var(--lyra-color-border)",
  border2:     "var(--app-border-strong)",
  ink:         "var(--lyra-color-text)",
  ink2:        "var(--app-text-2)",
  ink3:        "var(--lyra-color-text-muted)",
  ink4:        "var(--app-text-3)",
  accent:      "var(--lyra-color-primary)",
  accentLight: "var(--app-primary-soft)",
  accentMid:   "var(--app-primary-soft-2)",
  accentDark:  "var(--app-primary-deep)",
  green:       "var(--lyra-color-success)",
  greenBg:     "var(--lyra-color-success-bg)",
  greenBd:     "var(--app-success-border)",
  red:         "var(--lyra-color-danger)",
  redBg:       "var(--lyra-color-danger-bg)",
  redBd:       "var(--app-danger-border)",
  amber:       "var(--lyra-color-warning)",
  amberBg:     "var(--lyra-color-warning-bg)",
  amberBd:     "var(--app-warning-border)",
  blue:        "#1D4ED8",
  blueBg:      "var(--app-primary-soft)",
  blueBd:      "var(--app-primary-soft-2)",
};

const AVATAR_PALETTE = [
  { bg: T.accentLight, fg: T.accentDark },
  { bg: T.blueBg,       fg: T.blue },
  { bg: T.amberBg,      fg: T.amber },
  { bg: "var(--lyra-color-surface-2)",       fg: "#BE185D" },
  { bg: "var(--app-primary-soft)",       fg: "var(--lyra-color-primary)" },
];

const getAvatarColor = (name: string) => {
  const hash = name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
};

const getInitials = (name: string) =>
  name.trim().split(/\s+/).map(p => p[0]).slice(0, 2).join('').toUpperCase();

function Avatar({ name, size = 22 }: { name: string; size?: number }) {
  const { bg, fg } = getAvatarColor(name);
  return (
    <span
      style={{ width: size, height: size, background: bg, color: fg, fontSize: Math.round(size * 0.42) }}
      className="inline-flex items-center justify-center rounded-full font-semibold shrink-0"
      title={name}
    >
      {getInitials(name)}
    </span>
  );
}

const priorityMeta: Record<RetroItem['priority'], { label: string; fg: string; bg: string; bd: string }> = {
  high:   { label: 'High',   fg: T.red,   bg: T.redBg,   bd: T.redBd },
  medium: { label: 'Medium', fg: T.amber, bg: T.amberBg, bd: T.amberBd },
  low:    { label: 'Low',    fg: T.green, bg: T.greenBg, bd: T.greenBd },
};

// Reusable input/select/textarea styling — keeps every form field consistent.
const fieldClass =
  "w-full px-3 py-2 rounded-lg text-sm bg-white outline-none transition-colors";
const fieldStyle: React.CSSProperties = { border: `1px solid ${T.border2}`, color: T.ink };

function SprintRetroboard() {
  const [sprints, setSprints] = useState<Sprint[]>([
    {
      id: '1',
      name: 'Sprint 23 - Authentication & UI',
      startDate: '2025-07-29',
      endDate: '2025-08-09',
      team: ['John Doe', 'Jane Smith', 'Mike Johnson', 'Sarah Wilson'],
      isActive: true,
      items: [
        {
          id: '1',
          type: 'went-well',
          content: 'Successfully implemented dark mode theme switching',
          author: 'Jane Smith',
          votes: ['john', 'mike'],
          timestamp: '2025-08-09T10:30:00Z',
          priority: 'medium'
        },
        {
          id: '2',
          type: 'went-well',
          content: 'API integration went smoother than expected',
          author: 'John Doe',
          votes: ['jane', 'sarah', 'mike'],
          timestamp: '2025-08-09T11:15:00Z',
          priority: 'high'
        },
        {
          id: '3',
          type: 'could-improve',
          content: 'Need better communication during daily standups',
          author: 'Mike Johnson',
          votes: ['john', 'jane'],
          timestamp: '2025-08-09T11:45:00Z',
          priority: 'high'
        },
        {
          id: '4',
          type: 'could-improve',
          content: 'Code review process is taking too long',
          author: 'Sarah Wilson',
          votes: ['mike'],
          timestamp: '2025-08-09T12:00:00Z',
          priority: 'medium'
        },
        {
          id: '5',
          type: 'action-items',
          content: 'Set up automated testing pipeline',
          author: 'John Doe',
          votes: ['jane', 'mike', 'sarah'],
          timestamp: '2025-08-09T12:15:00Z',
          priority: 'high',
          status: 'pending',
          assignee: 'Mike Johnson'
        }
      ]
    },
    {
      id: '2',
      name: 'Sprint 22 - Database Setup',
      startDate: '2025-07-15',
      endDate: '2025-07-26',
      team: ['John Doe', 'Jane Smith', 'Mike Johnson'],
      isActive: false,
      items: []
    }
  ]);

  const [currentSprint, setCurrentSprint] = useState<Sprint | null>(null);
  const [showNewItemForm, setShowNewItemForm] = useState(false);
  const [showNewSprintForm, setShowNewSprintForm] = useState(false);
  const [newItemType, setNewItemType] = useState<RetroItem['type']>('went-well');
  const [viewMode, setViewMode] = useState<'board' | 'list'>('board');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState<'all' | 'high-votes' | 'my-items'>('all');

  const [newItem, setNewItem] = useState({
    content: '',
    author: 'Current User',
    priority: 'medium' as RetroItem['priority'],
    assignee: ''
  });

  const [newSprint, setNewSprint] = useState({
    name: '',
    startDate: '',
    endDate: '',
    team: [] as string[]
  });

  const teamMembers = ['John Doe', 'Jane Smith', 'Mike Johnson', 'Sarah Wilson', 'David Brown', 'Current User'];

  const retroColumns = [
    {
      type: 'went-well' as const,
      title: 'What Went Well',
      icon: ThumbsUp,
      fg: T.green,
      bg: T.greenBg,
      bd: T.greenBd,
    },
    {
      type: 'could-improve' as const,
      title: 'What Could Be Improved',
      icon: Lightbulb,
      fg: T.amber,
      bg: T.amberBg,
      bd: T.amberBd,
    },
    {
      type: 'action-items' as const,
      title: 'Action Items',
      icon: Target,
      fg: T.blue,
      bg: T.blueBg,
      bd: T.blueBd,
    }
  ];

  useEffect(() => {
    const activeSprint = sprints.find(s => s.isActive);
    setCurrentSprint(activeSprint || sprints[0] || null);
  }, [sprints]);

  const addRetroItem = () => {
    if (!newItem.content.trim() || !currentSprint) return;

    const item: RetroItem = {
      id: Date.now().toString(),
      type: newItemType,
      content: newItem.content,
      author: newItem.author,
      votes: [],
      timestamp: new Date().toISOString(),
      priority: newItem.priority,
      ...(newItemType === 'action-items' && {
        status: 'pending',
        assignee: newItem.assignee || undefined
      })
    };

    setSprints(prev => prev.map(sprint =>
      sprint.id === currentSprint.id
        ? { ...sprint, items: [...sprint.items, item] }
        : sprint
    ));

    setNewItem({
      content: '',
      author: 'Current User',
      priority: 'medium',
      assignee: ''
    });
    setShowNewItemForm(false);
  };

  const voteForItem = (itemId: string, userId: string = 'current-user') => {
    if (!currentSprint) return;

    setSprints(prev => prev.map(sprint =>
      sprint.id === currentSprint.id
        ? {
            ...sprint,
            items: sprint.items.map(item =>
              item.id === itemId
                ? {
                    ...item,
                    votes: item.votes.includes(userId)
                      ? item.votes.filter(id => id !== userId)
                      : [...item.votes, userId]
                  }
                : item
            )
          }
        : sprint
    ));
  };

  const updateActionItemStatus = (itemId: string, status: RetroItem['status']) => {
    if (!currentSprint) return;

    setSprints(prev => prev.map(sprint =>
      sprint.id === currentSprint.id
        ? {
            ...sprint,
            items: sprint.items.map(item =>
              item.id === itemId ? { ...item, status } : item
            )
          }
        : sprint
    ));
  };

  const deleteItem = (itemId: string) => {
    if (!currentSprint) return;

    setSprints(prev => prev.map(sprint =>
      sprint.id === currentSprint.id
        ? {
            ...sprint,
            items: sprint.items.filter(item => item.id !== itemId)
          }
        : sprint
    ));
  };

  const createNewSprint = () => {
    if (!newSprint.name.trim()) return;

    // Deactivate current active sprint
    setSprints(prev => prev.map(sprint => ({ ...sprint, isActive: false })));

    const sprint: Sprint = {
      id: Date.now().toString(),
      name: newSprint.name,
      startDate: newSprint.startDate,
      endDate: newSprint.endDate,
      team: newSprint.team,
      items: [],
      isActive: true
    };

    setSprints(prev => [sprint, ...prev]);
    setCurrentSprint(sprint);

    setNewSprint({
      name: '',
      startDate: '',
      endDate: '',
      team: []
    });
    setShowNewSprintForm(false);
  };

  const exportRetroData = () => {
    if (!currentSprint) return;

    const exportData = {
      sprint: {
        name: currentSprint.name,
        startDate: currentSprint.startDate,
        endDate: currentSprint.endDate,
        team: currentSprint.team
      },
      summary: {
        totalItems: currentSprint.items.length,
        wentWell: currentSprint.items.filter(i => i.type === 'went-well').length,
        improvements: currentSprint.items.filter(i => i.type === 'could-improve').length,
        actionItems: currentSprint.items.filter(i => i.type === 'action-items').length,
        completedActions: currentSprint.items.filter(i => i.type === 'action-items' && i.status === 'completed').length
      },
      items: currentSprint.items.map(item => ({
        type: item.type,
        content: item.content,
        author: item.author,
        votes: item.votes.length,
        priority: item.priority,
        ...(item.status && { status: item.status }),
        ...(item.assignee && { assignee: item.assignee })
      }))
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${currentSprint.name.replace(/\s+/g, '-')}-retrospective.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const filteredItems = currentSprint?.items.filter(item => {
    const matchesSearch = item.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.author.toLowerCase().includes(searchTerm.toLowerCase());

    let matchesFilter = true;
    if (filterBy === 'high-votes') {
      matchesFilter = item.votes.length >= 2;
    } else if (filterBy === 'my-items') {
      matchesFilter = item.author === 'Current User';
    }

    return matchesSearch && matchesFilter;
  }) || [];

  const getItemsByType = (type: RetroItem['type']) => {
    return filteredItems.filter(item => item.type === type)
      .sort((a, b) => b.votes.length - a.votes.length);
  };

  const getStatusIcon = (status?: RetroItem['status']) => {
    switch (status) {
      case 'completed': return <CheckCircle2 size={14} style={{ color: T.green }} />;
      case 'in-progress': return <Clock size={14} style={{ color: T.blue }} />;
      default: return <Target size={14} style={{ color: T.ink4 }} />;
    }
  };

  return (
    <div className="project-mgmt-page" style={{ minHeight: "100vh", background: T.page, fontFamily: "DM Sans, system-ui, sans-serif", padding: "28px 32px 60px" }}>
      <PageTracker pageTitle="Sprint Retroboard" category="project-management" />
      {/* Header */}
      <div className="mb-6">
        <div style={{ fontSize: 22, fontWeight: 600, color: T.ink, marginBottom: 4 }}>Sprint Retrospective Board</div>
        <p style={{ fontSize: 14, color: T.ink2 }}>
          Collaborative retrospective tool for agile teams to reflect and improve
        </p>
      </div>

      {/* Sprint Selector & Controls */}
      <div className="rounded-lg p-5 mb-6" style={{ background: T.surface, border: `1px solid ${T.border}` }}>
        <div className="flex flex-col xl:flex-row gap-4 items-start xl:items-center justify-between">
          {/* Sprint identity: picker + meta */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <select
                value={currentSprint?.id || ''}
                onChange={(e) => {
                  const sprint = sprints.find(s => s.id === e.target.value);
                  setCurrentSprint(sprint || null);
                }}
                className={fieldClass}
                style={{ ...fieldStyle, width: 'auto', fontWeight: 500 }}
              >
                {sprints.map(sprint => (
                  <option key={sprint.id} value={sprint.id}>
                    {sprint.name} {sprint.isActive ? '(Active)' : ''}
                  </option>
                ))}
              </select>

              <button
                onClick={() => setShowNewSprintForm(!showNewSprintForm)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                style={{ background: T.surface2, color: T.ink2, border: `1px solid ${T.border2}` }}
                onMouseEnter={(e) => (e.currentTarget.style.background = T.surface3)}
                onMouseLeave={(e) => (e.currentTarget.style.background = T.surface2)}
              >
                <Plus size={16} />
                New Sprint
              </button>
            </div>

            {currentSprint && (
              <div className="flex flex-wrap items-center gap-4 text-sm" style={{ color: T.ink3 }}>
                <span className="flex items-center gap-1.5">
                  <Calendar size={14} />
                  {new Date(currentSprint.startDate).toLocaleDateString()} – {new Date(currentSprint.endDate).toLocaleDateString()}
                </span>
                <span className="flex items-center gap-1.5">
                  <Users size={14} />
                  {currentSprint.team.length} team members
                </span>
                <span className="flex items-center gap-1.5">
                  <MessageSquare size={14} />
                  {currentSprint.items.length} items
                </span>
              </div>
            )}
          </div>

          {/* Item controls: search / filter / view / export */}
          <div className="flex flex-wrap gap-2 items-center pt-3 xl:pt-0 xl:pl-4 border-t xl:border-t-0 xl:border-l w-full xl:w-auto" style={{ borderColor: T.border }}>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: T.ink4 }} />
              <input
                type="text"
                placeholder="Search items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={fieldClass}
                style={{ ...fieldStyle, paddingLeft: 34, width: 180 }}
              />
            </div>

            <select
              value={filterBy}
              onChange={(e) => setFilterBy(e.target.value as any)}
              className={fieldClass}
              style={{ ...fieldStyle, width: 'auto' }}
            >
              <option value="all">All Items</option>
              <option value="high-votes">High Votes (2+)</option>
              <option value="my-items">My Items</option>
            </select>

            <div className="flex rounded-lg overflow-hidden" style={{ border: `1px solid ${T.border2}` }}>
              <button
                onClick={() => setViewMode('board')}
                className="p-2 transition-colors"
                style={viewMode === 'board' ? { background: T.accent, color: 'var(--app-on-accent)' } : { background: T.surface, color: T.ink3 }}
                aria-label="Board view"
              >
                <Grid3X3 size={16} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className="p-2 transition-colors"
                style={viewMode === 'list' ? { background: T.accent, color: 'var(--app-on-accent)' } : { background: T.surface, color: T.ink3 }}
                aria-label="List view"
              >
                <List size={16} />
              </button>
            </div>

            <button
              onClick={exportRetroData}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
              style={{ background: T.surface, color: T.accent, border: `1px solid ${T.accent}` }}
              onMouseEnter={(e) => (e.currentTarget.style.background = T.accentLight)}
              onMouseLeave={(e) => (e.currentTarget.style.background = T.surface)}
            >
              <Download size={16} />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* New Sprint Form */}
      {showNewSprintForm && (
        <div className="rounded-lg p-6 mb-6" style={{ background: T.surface, border: `1px solid ${T.border}` }}>
          <h3 className="text-base font-semibold mb-4" style={{ color: T.ink }}>Create New Sprint</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2" style={{ color: T.ink2 }}>
                Sprint Name *
              </label>
              <input
                type="text"
                value={newSprint.name}
                onChange={(e) => setNewSprint(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Sprint 24 - User Dashboard"
                className={fieldClass}
                style={fieldStyle}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: T.ink2 }}>
                Start Date
              </label>
              <input
                type="date"
                value={newSprint.startDate}
                onChange={(e) => setNewSprint(prev => ({ ...prev, startDate: e.target.value }))}
                className={fieldClass}
                style={fieldStyle}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: T.ink2 }}>
                End Date
              </label>
              <input
                type="date"
                value={newSprint.endDate}
                onChange={(e) => setNewSprint(prev => ({ ...prev, endDate: e.target.value }))}
                className={fieldClass}
                style={fieldStyle}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2" style={{ color: T.ink2 }}>
                Team Members
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {teamMembers.map(member => (
                  <label key={member} className="flex items-center text-sm gap-2" style={{ color: T.ink2 }}>
                    <input
                      type="checkbox"
                      checked={newSprint.team.includes(member)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewSprint(prev => ({ ...prev, team: [...prev.team, member] }));
                        } else {
                          setNewSprint(prev => ({ ...prev, team: prev.team.filter(m => m !== member) }));
                        }
                      }}
                      style={{ accentColor: T.accent }}
                    />
                    {member}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={createNewSprint}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors"
              style={{ background: T.accent }}
              onMouseEnter={(e) => (e.currentTarget.style.background = T.accentDark)}
              onMouseLeave={(e) => (e.currentTarget.style.background = T.accent)}
            >
              Create Sprint
            </button>
            <button
              onClick={() => setShowNewSprintForm(false)}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              style={{ background: T.surface, color: T.ink2, border: `1px solid ${T.border2}` }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {currentSprint ? (
        <>
          {/* Add Item Button */}
          <div className="flex justify-center mb-6">
            <button
              onClick={() => setShowNewItemForm(!showNewItemForm)}
              className="flex items-center gap-2 px-6 py-3 rounded-lg text-white font-medium transition-colors"
              style={{ background: T.accent }}
              onMouseEnter={(e) => (e.currentTarget.style.background = T.accentDark)}
              onMouseLeave={(e) => (e.currentTarget.style.background = T.accent)}
            >
              <Plus size={20} />
              Add Retrospective Item
            </button>
          </div>

          {/* New Item Form */}
          {showNewItemForm && (
            <div className="rounded-lg p-6 mb-6" style={{ background: T.surface, border: `1px solid ${T.border}` }}>
              <h3 className="text-base font-semibold mb-4" style={{ color: T.ink }}>Add New Item</h3>

              {/* Item Type Selection */}
              <div className="flex flex-wrap gap-2 mb-4">
                {retroColumns.map(column => {
                  const Icon = column.icon;
                  const active = newItemType === column.type;
                  return (
                    <button
                      key={column.type}
                      onClick={() => setNewItemType(column.type)}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                      style={active
                        ? { background: column.bg, color: column.fg, border: `1px solid ${column.bd}` }
                        : { background: T.surface2, color: T.ink3, border: `1px solid ${T.surface2}` }}
                    >
                      <Icon size={16} />
                      {column.title}
                    </button>
                  );
                })}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2" style={{ color: T.ink2 }}>
                    Content *
                  </label>
                  <textarea
                    value={newItem.content}
                    onChange={(e) => setNewItem(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Describe what went well, what could be improved, or action items..."
                    rows={3}
                    className={`${fieldClass} resize-none`}
                    style={fieldStyle}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: T.ink2 }}>
                    Priority
                  </label>
                  <select
                    value={newItem.priority}
                    onChange={(e) => setNewItem(prev => ({ ...prev, priority: e.target.value as RetroItem['priority'] }))}
                    className={fieldClass}
                    style={fieldStyle}
                  >
                    <option value="low">Low Priority</option>
                    <option value="medium">Medium Priority</option>
                    <option value="high">High Priority</option>
                  </select>
                </div>

                {newItemType === 'action-items' && (
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: T.ink2 }}>
                      Assignee
                    </label>
                    <select
                      value={newItem.assignee}
                      onChange={(e) => setNewItem(prev => ({ ...prev, assignee: e.target.value }))}
                      className={fieldClass}
                      style={fieldStyle}
                    >
                      <option value="">Select assignee...</option>
                      {currentSprint.team.map(member => (
                        <option key={member} value={member}>{member}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={addRetroItem}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors"
                  style={{ background: T.accent }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = T.accentDark)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = T.accent)}
                >
                  Add Item
                </button>
                <button
                  onClick={() => setShowNewItemForm(false)}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  style={{ background: T.surface, color: T.ink2, border: `1px solid ${T.border2}` }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Retrospective Board */}
          {viewMode === 'board' ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              {retroColumns.map(column => {
                const Icon = column.icon;
                const items = getItemsByType(column.type);

                return (
                  <div
                    key={column.type}
                    className="rounded-lg flex flex-col"
                    style={{ background: T.surface, border: `1px solid ${T.border}`, borderTop: `3px solid ${column.bd}`, minHeight: 384 }}
                  >
                    <div className="flex items-center gap-2.5 px-4 py-3.5" style={{ borderBottom: `1px solid ${T.border}` }}>
                      <span
                        className="inline-flex items-center justify-center rounded-full shrink-0"
                        style={{ width: 28, height: 28, background: column.bg, color: column.fg }}
                      >
                        <Icon size={15} />
                      </span>
                      <h3 className="font-semibold text-sm flex-1" style={{ color: T.ink }}>
                        {column.title}
                      </h3>
                      <span
                        className="px-2 py-0.5 rounded-full text-xs font-medium"
                        style={{ background: T.surface2, color: T.ink3 }}
                      >
                        {items.length}
                      </span>
                    </div>

                    <div className="p-3.5 space-y-2.5 flex-1">
                      {items.map(item => {
                        const pMeta = priorityMeta[item.priority];
                        return (
                        <div
                          key={item.id}
                          className="p-3.5 rounded-lg shadow-sm hover:shadow-md transition-shadow"
                          style={{ background: T.surface, border: `1px solid ${T.border}`, borderLeft: `3px solid ${pMeta.fg}` }}
                        >
                          <div className="flex items-start justify-between gap-2 mb-2.5">
                            <p className="text-sm leading-relaxed flex-1" style={{ color: T.ink }}>
                              {item.content}
                            </p>
                            <button
                              onClick={() => deleteItem(item.id)}
                              className="p-1 rounded transition-colors shrink-0"
                              style={{ color: T.ink4 }}
                              onMouseEnter={(e) => (e.currentTarget.style.color = T.red)}
                              onMouseLeave={(e) => (e.currentTarget.style.color = T.ink4)}
                              aria-label="Delete item"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>

                          <div className="flex items-center flex-wrap gap-2 mb-2.5">
                            <span className="flex items-center gap-1.5 text-xs" style={{ color: T.ink3 }}>
                              <Avatar name={item.author} size={18} />
                              {item.author}
                            </span>
                            <span
                              className="px-1.5 py-0.5 rounded text-[10.5px] font-medium"
                              style={{ background: pMeta.bg, color: pMeta.fg }}
                            >
                              {pMeta.label}
                            </span>
                            <span className="text-xs ml-auto" style={{ color: T.ink4 }}>
                              {new Date(item.timestamp).toLocaleDateString()}
                            </span>
                          </div>

                          {item.assignee && (
                            <div className="flex items-center gap-1.5 text-xs mb-2.5" style={{ color: T.blue }}>
                              <Avatar name={item.assignee} size={18} />
                              Assigned to {item.assignee}
                            </div>
                          )}

                          <div className="flex items-center justify-between pt-2" style={{ borderTop: `1px solid ${T.border}` }}>
                            <button
                              onClick={() => voteForItem(item.id)}
                              className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors"
                              style={item.votes.includes('current-user')
                                ? { background: T.accent, color: 'var(--app-on-accent)' }
                                : { background: T.surface2, color: T.ink3 }}
                            >
                              <ThumbsUp size={12} />
                              {item.votes.length}
                            </button>

                            {item.type === 'action-items' && (
                              <div className="flex items-center gap-1.5">
                                {getStatusIcon(item.status)}
                                <select
                                  value={item.status || 'pending'}
                                  onChange={(e) => updateActionItemStatus(item.id, e.target.value as RetroItem['status'])}
                                  className="text-xs rounded px-1.5 py-1 outline-none"
                                  style={{ border: `1px solid ${T.border2}`, color: T.ink2, background: T.surface }}
                                >
                                  <option value="pending">Pending</option>
                                  <option value="in-progress">In Progress</option>
                                  <option value="completed">Completed</option>
                                </select>
                              </div>
                            )}
                          </div>
                        </div>
                        );
                      })}

                      {items.length === 0 && (
                        <div className="text-center py-8 rounded-lg border-2 border-dashed" style={{ color: T.ink4, borderColor: T.border }}>
                          <Icon size={28} className="mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No items yet</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* List View */
            <div className="rounded-lg overflow-hidden" style={{ background: T.surface, border: `1px solid ${T.border}` }}>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead style={{ background: T.surface2 }}>
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: T.ink3 }}>Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: T.ink3 }}>Content</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: T.ink3 }}>Author</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: T.ink3 }}>Votes</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: T.ink3 }}>Priority</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: T.ink3 }}>Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: T.ink3 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map(item => {
                      const column = retroColumns.find(c => c.type === item.type);
                      const Icon = column?.icon || MessageSquare;
                      const pMeta = priorityMeta[item.priority];

                      return (
                        <tr key={item.id} style={{ borderTop: `1px solid ${T.border}` }}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div
                              className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium w-fit"
                              style={{ background: column?.bg, color: column?.fg }}
                            >
                              <Icon size={13} />
                              {column?.title.split(' ')[0]}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm max-w-md" style={{ color: T.ink }}>
                              {item.content}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="flex items-center gap-1.5 text-sm" style={{ color: T.ink3 }}>
                              <Avatar name={item.author} size={18} />
                              {item.author}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => voteForItem(item.id)}
                              className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors"
                              style={item.votes.includes('current-user')
                                ? { background: T.accent, color: 'var(--app-on-accent)' }
                                : { background: T.surface2, color: T.ink3 }}
                            >
                              <ThumbsUp size={12} />
                              {item.votes.length}
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className="px-2 py-1 rounded text-xs font-medium"
                              style={{ background: pMeta.bg, color: pMeta.fg }}
                            >
                              {pMeta.label}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {item.type === 'action-items' ? (
                              <select
                                value={item.status || 'pending'}
                                onChange={(e) => updateActionItemStatus(item.id, e.target.value as RetroItem['status'])}
                                className="text-xs rounded px-2 py-1 outline-none"
                                style={{ border: `1px solid ${T.border2}`, color: T.ink2, background: T.surface }}
                              >
                                <option value="pending">Pending</option>
                                <option value="in-progress">In Progress</option>
                                <option value="completed">Completed</option>
                              </select>
                            ) : (
                              <span style={{ color: T.ink4 }}>—</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => deleteItem(item.id)}
                              className="transition-colors"
                              style={{ color: T.ink4 }}
                              onMouseEnter={(e) => (e.currentTarget.style.color = T.red)}
                              onMouseLeave={(e) => (e.currentTarget.style.color = T.ink4)}
                              aria-label="Delete item"
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {filteredItems.length === 0 && (
                  <div className="text-center py-12">
                    <MessageSquare size={44} className="mx-auto mb-4" style={{ color: T.ink4 }} />
                    <h3 className="text-base font-semibold mb-2" style={{ color: T.ink }}>No items found</h3>
                    <p style={{ color: T.ink3 }}>
                      {searchTerm || filterBy !== 'all'
                        ? 'Try adjusting your search or filters'
                        : 'Add your first retrospective item to get started'
                      }
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Sprint Summary */}
          <div className="mt-6 rounded-lg p-6" style={{ background: T.surface, border: `1px solid ${T.border}` }}>
            <h3 className="text-base font-semibold mb-4 flex items-center gap-2" style={{ color: T.ink }}>
              <Zap size={18} style={{ color: T.accent }} />
              Sprint Summary
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 rounded-lg" style={{ background: T.greenBg }}>
                <div className="text-2xl font-bold" style={{ color: T.green }}>
                  {getItemsByType('went-well').length}
                </div>
                <div className="text-sm" style={{ color: T.ink3 }}>Went Well</div>
              </div>
              <div className="text-center p-4 rounded-lg" style={{ background: T.amberBg }}>
                <div className="text-2xl font-bold" style={{ color: T.amber }}>
                  {getItemsByType('could-improve').length}
                </div>
                <div className="text-sm" style={{ color: T.ink3 }}>Improvements</div>
              </div>
              <div className="text-center p-4 rounded-lg" style={{ background: T.blueBg }}>
                <div className="text-2xl font-bold" style={{ color: T.blue }}>
                  {getItemsByType('action-items').length}
                </div>
                <div className="text-sm" style={{ color: T.ink3 }}>Action Items</div>
              </div>
              <div className="text-center p-4 rounded-lg" style={{ background: T.accentLight }}>
                <div className="text-2xl font-bold" style={{ color: T.accentDark }}>
                  {currentSprint.items.filter(i => i.type === 'action-items' && i.status === 'completed').length}
                </div>
                <div className="text-sm" style={{ color: T.ink3 }}>Completed Actions</div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="rounded-lg p-12 text-center" style={{ background: T.surface, border: `1px solid ${T.border}` }}>
          <MessageSquare size={44} className="mx-auto mb-4" style={{ color: T.ink4 }} />
          <h3 className="text-base font-semibold mb-2" style={{ color: T.ink }}>No Sprint Selected</h3>
          <p className="mb-4" style={{ color: T.ink3 }}>
            Create your first sprint to start your retrospective
          </p>
          <button
            onClick={() => setShowNewSprintForm(true)}
            className="px-4 py-2 rounded-lg text-white font-medium transition-colors"
            style={{ background: T.accent }}
            onMouseEnter={(e) => (e.currentTarget.style.background = T.accentDark)}
            onMouseLeave={(e) => (e.currentTarget.style.background = T.accent)}
          >
            Create First Sprint
          </button>
        </div>
      )}
    </div>
  );
}

export default SprintRetroboard;
